<template>
	<div v-if="!showBillingLocationFields" class="payment-method">
		<div class="payment-method__header">
			<div v-if="!showCategories" class="payment-method__header__title">
				{{ $t('checkout.payment-method') }}
			</div>
			<div v-if="showChooseAnotherWay" class="payment-method__header__action">
				<button class="btn-link-blue" @click="chooseAnotherMethod">
					{{ $t('checkout.choose-another-way') }}
					<svgIcon icon-holder-class="icon-faq" icon-id="icon-page-right-blue" />
				</button>
			</div>
		</div>
		<div class="payment-method__body">
			<FraudErrors v-if="isFraudErrorCode" />
			<SelectedPaymentMethod v-else-if="selectedPg && showSelectedPg" />
			<AllPaymentMethods
				v-else-if="!(selectedPg && showSelectedPg)"
				:loading="pgLoading"
				:show-categories="showCategories"
				@click-pg="handleSelectPG"
			/>
		</div>
	</div>
</template>

<script>
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import AllPaymentMethods from './pg-button-group';
import SelectedPaymentMethod from './selected-payment-method';
import FraudErrors from './fraud-errors';
import { mapGetters } from 'vuex';

export default {
	name: 'PaymentMethod',
	components: {
		svgIcon,
		AllPaymentMethods,
		SelectedPaymentMethod,
		FraudErrors,
	},
	computed: {
		...mapGetters({
			isFraudErrorCode: 'isFraudErrorCode',
		}),
		showBillingLocationFields() {
			return this.$store.state.showBillingLocationFields;
		},
		showChooseAnotherWay() {
			return !this.showBillingLocationFields && this.showSelectedPg && !this.isFraudErrorCode;
		},
		selectedPg() {
			return this.$store.state.selectedPg;
		},
		showSelectedPg() {
			return this.$store.state.showSelectedPg;
		},
		products() {
			return this.$store.state.productsData;
		},
		pgLoading: {
			get() {
				return this.$store.state.pgLoading;
			},
			set(value) {
				this.$store.commit('setPgLoading', value);
			},
		},
		isIngamePurchase() {
			return this.$route.name === 'in-game-purchase' || this.$store.state.appType === 'game';
		},
		showCategories() {
			return this.$store.state.paymentMethodsList && this.$store.state.paymentMethodsList.categoryDisplay;
		},
	},
	methods: {
		handleSelectPG(pgDetails) {
			this.$a2s.click('purchase_method_select', {
				method: pgDetails.displayName,
			});
			if (!this.pgLoading) {
				this.$store.commit('setSelectedPg', pgDetails);
				this.$store.commit('setPgClicked', true);
				this.pgLoading = true;

				if (this.isIngamePurchase) {
					let igpAmount, igpCurrency;
					if (this.products && this.products[0] && this.products[0].productType === 'nxwallet') {
						igpAmount = this.products[0].value;
						igpCurrency = 'NX';
					} else if (this.$store.state.purchaseSummaryData) {
						igpAmount = this.$store.state.purchaseSummaryData.subTotalAmount;
						igpCurrency = this.$store.state.purchaseSummaryData.currency;
					}
					dataLayer.push({
						event: 'In Game Purchase PG Select',
						IGP_amount: igpAmount,
						IGP_currency: igpCurrency,
						IGP_paymentGateway: pgDetails.methodName,
						IGP_autoPG: false,
						...(this.$route.query.from && this.$route.query.from === 'add-more-funds' // if user comes from the add more funds page (th insufficient result for truemoney/12call) and selects pg
							? { IGP_addMore: true }
							: {}),
					});
				} else {
					dataLayer.push({
						event: 'Add Balance Select PG',
						'Returning Check': 'Manual',
						'Payment Gateway': pgDetails.methodName,
						'PG Type': pgDetails.isRefundable ? 'Credit' : 'PrePaid',
						'NX Amount': this.products[0].value,
					});

					dataLayer.push({ ecommerce: null });

					dataLayer.push({
						event: 'checkout',
						ecommerce: {
							currencyCode: this.products[0].currency,
							checkout: {
								actionField: { step: 2, option: pgDetails.displayName },
								products: [
									{
										name: this.products[0].name,
										variant: pgDetails.isRefundable ? 'Credit' : 'PrePaid',
										price: this.products[0].amount,
										category: 'NX',
										quantity: 1,
									},
								],
							},
						},
					});
				}

				this.$store
					.dispatch('beginPayment')
					.then(() => {
						this.pgLoading = false;
					})
					.catch((error) => {
						this.pgLoading = false;

						const errorCode =
							error.response && error.response.data && error.response.data.code
								? error.response.data.code
								: error.response.status || '500';

						if (this.isIngamePurchase) {
							this.$router.replace({
								name: 'in-game-purchase-failed',
								query: {
									...(errorCode && { error_code: errorCode }),
								},
							});
						} else {
							this.$router.replace({
								name: 'purchase-failed',
								params: {
									errorCode: errorCode,
								},
							});
						}
					});
			}
		},
		chooseAnotherMethod() {
			this.$a2s.click('karma_change_method');
			dataLayer.push({
				event: `${this.isIngamePurchase ? 'In Game' : 'Add Balance'} Choose Another Way Click`,
			});

			this.$store.commit('setPgLoading', false);
			this.$store.commit('setShowSelectedPg', false);
			this.$store.commit('setSelectedPg', null);
			this.$store.commit('setKarmakoinSelectedOption', null); // remove karmakoin selected option
		},
	},
};
</script>

<style lang="less" scoped>
.payment-method {
	flex: 0 0 100%;
	max-width: 100%;
	&__header {
		color: #fff;
		display: flex;
		flex-wrap: wrap;
		align-items: flex-end;
		font-size: 18px;
		font-weight: bold;
		&__title {
			margin-right: 20px;
			padding-bottom: 16px;
		}
		&__action {
			display: flex;
			flex-wrap: nowrap;
			flex-basis: auto;
			flex-grow: 1;
			max-width: 100%;
			justify-content: flex-end;
			align-items: center;
			font-size: 16px;
			font-weight: normal;
			white-space: nowrap;
			padding-bottom: 17px;
			.btn-link-blue {
				margin-left: 5px;
				text-transform: uppercase;
			}
		}
	}
}

@media (max-width: 599px) {
	.payment-method {
		&__header {
			&__title,
			&__action {
				padding-bottom: 13px;
			}
		}
	}
}
</style>
